import React, { useMemo } from 'react';
import { BarChart, Bar, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import DonutChart from './DonutChart';
import { scheduleByYear } from '../mock';
import { formatMoneyShort, currencies } from '../lib/currency';
import { useAppSettings } from '../lib/i18n';

/**
 * Unified breakup chart. Renders one of donut | bar | line.
 * schedule + summary drive the aggregation for bar/line, principal/interest
 * totals drive the donut.
 */
export default function BreakupChart({
  type = 'donut',
  principal,
  interest,
  moratoriumInterest = 0,
  schedule = [],
  size = 320,
  chartId,
}) {
  const { currency, t } = useAppSettings();
  const c = currencies[currency];
  const yearly = useMemo(() => scheduleByYear(schedule), [schedule]);
  const hasMorInt = yearly.some((y) => (y.moratoriumInterest || 0) > 0);

  if (type === 'donut' || yearly.length === 0) {
    return (
      <DonutChart
        principal={principal}
        interest={interest}
        moratoriumInterest={moratoriumInterest}
        size={size}
        chartId={chartId}
      />
    );
  }

  const height = Math.max(260, size * 0.85);
  const tickFmt = (v) => formatMoneyShort(v, currency).replace(c.symbol, c.symbol + ' ');
  const tooltipFmt = (v, name) => [formatMoneyShort(v, currency), name];

  return (
    <div id={chartId} className="w-full" style={{ height, minHeight: 260, minWidth: 200 }}>
      <ResponsiveContainer width="100%" height="100%" minHeight={260} minWidth={200}>
        {type === 'bar' ? (
          <BarChart data={yearly} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
            <XAxis dataKey="year" tick={{ fill: '#64748B', fontSize: 11 }} label={{ value: 'Year', position: 'insideBottom', offset: -2, fill: '#64748B', fontSize: 11 }} />
            <YAxis tickFormatter={tickFmt} tick={{ fill: '#64748B', fontSize: 10 }} width={70} />
            <Tooltip formatter={tooltipFmt} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Bar dataKey="principal" name={t('principal')} stackId="a" fill="#0176D3" radius={[0, 0, 0, 0]} />
            <Bar dataKey="interest" name={t('total_interest')} stackId="a" fill="#F97066" radius={hasMorInt ? [0, 0, 0, 0] : [4, 4, 0, 0]} />
            {hasMorInt && (
              <Bar dataKey="moratoriumInterest" name={t('moratorium_interest_added')} stackId="a" fill="#F59E0B" radius={[4, 4, 0, 0]} />
            )}
          </BarChart>
        ) : (
          <LineChart data={yearly} margin={{ top: 8, right: 8, left: 8, bottom: 8 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(148,163,184,0.25)" />
            <XAxis dataKey="year" tick={{ fill: '#64748B', fontSize: 11 }} label={{ value: 'Year', position: 'insideBottom', offset: -2, fill: '#64748B', fontSize: 11 }} />
            <YAxis tickFormatter={tickFmt} tick={{ fill: '#64748B', fontSize: 10 }} width={70} />
            <Tooltip formatter={tooltipFmt} contentStyle={{ borderRadius: 8, border: '1px solid #E2E8F0' }} />
            <Legend wrapperStyle={{ fontSize: 12 }} />
            <Line type="monotone" dataKey="principal" name={t('principal')} stroke="#0176D3" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            <Line type="monotone" dataKey="interest" name={t('total_interest')} stroke="#F97066" strokeWidth={2.5} dot={{ r: 3 }} activeDot={{ r: 5 }} />
            {hasMorInt && (
              <Line type="monotone" dataKey="moratoriumInterest" name={t('moratorium_interest_added')} stroke="#F59E0B" strokeWidth={2.5} strokeDasharray="4 3" dot={{ r: 3 }} activeDot={{ r: 5 }} />
            )}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
}

export function ChartTypeToggle({ value, onChange, options, size = 'md', className = '' }) {
  const opts = options || [
    { key: 'donut', label: 'Donut' },
    { key: 'bar', label: 'Bar' },
    { key: 'line', label: 'Line' },
  ];
  const pad = size === 'sm' ? 'px-2.5 py-1 text-xs' : 'px-3 py-1.5 text-xs';
  return (
    <div className={`inline-flex rounded-lg overflow-hidden border-2 border-slate-200 dark:border-[#1E2A44] shadow-sm ${className}`}>
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onChange(o.key)}
          className={`${pad} font-semibold transition-colors ${
            value === o.key
              ? 'bg-[#0176D3] text-white'
              : 'bg-white dark:bg-[#0F1B36] text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-[#152343]'
          }`}
          data-testid={`chart-toggle-${o.key}`}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}
